import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Calculator, 
  TrendingUp, 
  ArrowUp, 
  ArrowDown,
  Minus,
  Euro,
  Target,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Utensils,
  Wine
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Preisvorschlag = () => {
  const [wunschGewinnMonat, setWunschGewinnMonat] = useState("");
  const [wunschGewinnJahr, setWunschGewinnJahr] = useState("");
  const [ergebnis, setErgebnis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kostenUebersicht, setKostenUebersicht] = useState(null);

  useEffect(() => {
    fetchKostenUebersicht();
  }, []);

  const fetchKostenUebersicht = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/summary`, { withCredentials: true });
      setKostenUebersicht(res.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const berechnePreisvorschlaege = async () => {
    if (!wunschGewinnMonat && !wunschGewinnJahr) {
      return;
    }

    setLoading(true);
    try {
      const payload = {};
      if (wunschGewinnMonat) {
        payload.wunsch_gewinn_monat = parseFloat(wunschGewinnMonat);
      } else if (wunschGewinnJahr) {
        payload.wunsch_gewinn_jahr = parseFloat(wunschGewinnJahr);
      }

      const res = await axios.post(
        `${API}/tools/preisvorschlag-gewinnziel`,
        payload,
        { withCredentials: true }
      );
      setErgebnis(res.data);
    } catch (error) {
      console.error("Error calculating prices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMonatChange = (value) => {
    setWunschGewinnMonat(value);
    if (value) {
      setWunschGewinnJahr((parseFloat(value) * 12).toFixed(0));
    } else {
      setWunschGewinnJahr("");
    }
  };

  const handleJahrChange = (value) => {
    setWunschGewinnJahr(value);
    if (value) {
      setWunschGewinnMonat((parseFloat(value) / 12).toFixed(0));
    } else {
      setWunschGewinnMonat("");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getAnpassungIcon = (richtung) => {
    switch (richtung) {
      case 'erhöhen':
        return <ArrowUp size={16} className="text-[#D96C4A]" />;
      case 'senken':
        return <ArrowDown size={16} className="text-[#2E7D32]" />;
      default:
        return <Minus size={16} className="text-[#858585]" />;
    }
  };

  const getAnpassungClass = (richtung) => {
    switch (richtung) {
      case 'erhöhen':
        return 'bg-[#FFF3E0] border-[#FFE0B2] text-[#E65100]';
      case 'senken':
        return 'bg-[#E8F5E9] border-[#C8E6C9] text-[#2E7D32]';
      default:
        return 'bg-stone-50 border-stone-200 text-[#5C5C5C]';
    }
  };

  return (
    <div data-testid="preisvorschlag-page">
      {/* Header */}
      <header className="page-header">
        <h1 className="page-title">Preisvorschläge</h1>
        <p className="page-subtitle">Berechne optimale Verkaufspreise basierend auf deinem Wunschgewinn</p>
      </header>

      {/* MwSt Info Banner */}
      <div className="px-8 pt-4">
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
          <AlertCircle size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <div>
            <span className="font-medium text-blue-800 dark:text-blue-300">MwSt-Hinweis (Deutschland):</span>
            <span className="text-blue-700 dark:text-blue-400 ml-1">
              Speisen: <strong>7%</strong> (ermäßigt) | Getränke: <strong>19%</strong> (regulär)
            </span>
          </div>
        </div>
      </div>

      {/* Eingabe Section */}
      <div className="px-8 py-4">
        <div className="bg-white border border-stone-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-[#E8F5E9] text-[#3A4D39]">
              <Target size={20} />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-[#3A4D39]">Wunschgewinn eingeben</h2>
              <p className="text-sm text-[#858585]">Nach Abzug aller Kosten (Fixkosten + Personal)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label htmlFor="gewinn-monat" className="text-sm font-medium mb-2 block">
                Wunschgewinn pro Monat
              </Label>
              <div className="relative">
                <Input
                  id="gewinn-monat"
                  type="number"
                  value={wunschGewinnMonat}
                  onChange={(e) => handleMonatChange(e.target.value)}
                  placeholder="z.B. 3000"
                  className="pr-10"
                  data-testid="input-gewinn-monat"
                />
                <Euro size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#858585]" />
              </div>
            </div>

            <div>
              <Label htmlFor="gewinn-jahr" className="text-sm font-medium mb-2 block">
                Wunschgewinn pro Jahr
              </Label>
              <div className="relative">
                <Input
                  id="gewinn-jahr"
                  type="number"
                  value={wunschGewinnJahr}
                  onChange={(e) => handleJahrChange(e.target.value)}
                  placeholder="z.B. 36000"
                  className="pr-10"
                  data-testid="input-gewinn-jahr"
                />
                <Euro size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#858585]" />
              </div>
            </div>
          </div>

          {/* Kosten Info */}
          {kostenUebersicht && (
            <div className="bg-stone-50 border border-stone-200 p-4 mb-6">
              <p className="text-xs uppercase tracking-wide text-[#858585] mb-3">Deine aktuellen monatlichen Kosten</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-[#5C5C5C]">Personalkosten:</span>
                  <p className="font-mono font-medium">{formatCurrency(kostenUebersicht.mitarbeiter?.gesamt_kosten_monatlich)}</p>
                </div>
                <div>
                  <span className="text-[#5C5C5C]">Fixkosten:</span>
                  <p className="font-mono font-medium">{formatCurrency(kostenUebersicht.fixkosten?.gesamt_monatlich)}</p>
                </div>
                <div>
                  <span className="text-[#5C5C5C]">Gesamt:</span>
                  <p className="font-mono font-medium text-[#3A4D39]">{formatCurrency(kostenUebersicht.gesamt?.kosten_monatlich)}</p>
                </div>
                <div>
                  <span className="text-[#5C5C5C]">Produkte:</span>
                  <p className="font-mono font-medium">{kostenUebersicht.speisen?.anzahl + kostenUebersicht.getraenke?.anzahl || 0}</p>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={berechnePreisvorschlaege}
            disabled={loading || (!wunschGewinnMonat && !wunschGewinnJahr)}
            className="w-full md:w-auto bg-[#3A4D39] hover:bg-[#2D3B2D] text-white"
            data-testid="btn-berechnen"
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Berechne...
              </>
            ) : (
              <>
                <Calculator size={16} className="mr-2" />
                Preisvorschläge berechnen
              </>
            )}
          </Button>
        </div>

        {/* Ergebnis */}
        {ergebnis && (
          <>
            {/* Hinweis wenn keine Produkte vorhanden */}
            {ergebnis.hinweis && (
              <div className="bg-[#FFF3E0] border border-[#FFE0B2] p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-[#E65100] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#E65100] text-sm">Hinweis</p>
                    <p className="text-sm text-[#BF360C] mt-1">{ergebnis.hinweis}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Kosten-Übersicht (immer anzeigen) */}
            {ergebnis.kosten_uebersicht && (
              <div className="bg-white border border-stone-200 p-5 mb-6">
                <h3 className="font-display text-lg font-semibold text-[#3A4D39] mb-4">Kosten-Übersicht</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#858585]">Fixkosten/Monat</p>
                    <p className="font-mono text-lg font-medium">{formatCurrency(ergebnis.kosten_uebersicht.fixkosten_monat)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#858585]">Personalkosten/Monat</p>
                    <p className="font-mono text-lg font-medium">{formatCurrency(ergebnis.kosten_uebersicht.personalkosten_monat)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#858585]">Gesamt Kosten</p>
                    <p className="font-mono text-lg font-medium text-[#3A4D39]">{formatCurrency(ergebnis.kosten_uebersicht.gesamt_kosten_monat)}</p>
                  </div>
                  {ergebnis.kosten_uebersicht.notwendiger_deckungsbeitrag && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-[#858585]">Deckungsbeitrag nötig</p>
                      <p className="font-mono text-lg font-medium text-[#D96C4A]">{formatCurrency(ergebnis.kosten_uebersicht.notwendiger_deckungsbeitrag)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Berechnung Details - nur wenn vorhanden */}
            {ergebnis.berechnung && (
              <>
                {/* Zusammenfassung */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white border border-stone-200 p-5">
                    <p className="text-xs uppercase tracking-wide text-[#858585] mb-2">Notwendiger Deckungsbeitrag</p>
                    <p className="font-mono text-2xl font-medium text-[#3A4D39]">
                      {formatCurrency(ergebnis.berechnung.notwendiger_deckungsbeitrag_monat)}
                    </p>
                    <p className="text-xs text-[#858585] mt-1">pro Monat</p>
                  </div>

                  <div className="bg-white border border-stone-200 p-5">
                    <p className="text-xs uppercase tracking-wide text-[#858585] mb-2">Notwendiger Umsatz</p>
                    <p className="font-mono text-2xl font-medium text-[#1A1A1A]">
                      {formatCurrency(ergebnis.berechnung.notwendiger_umsatz_monat)}
                    </p>
                    <p className="text-xs text-[#858585] mt-1">pro Monat</p>
                  </div>

                  <div className="bg-white border border-stone-200 p-5">
                    <p className="text-xs uppercase tracking-wide text-[#858585] mb-2">Durchschnittliche Zielmarge</p>
                    <p className="font-mono text-2xl font-medium text-[#D96C4A]">
                      {ergebnis.berechnung.durchschnittliche_zielmarge_prozent}%
                    </p>
                    <p className="text-xs text-[#858585] mt-1">auf Wareneinsatz</p>
                  </div>
                </div>
              </>
            )}

            {/* Status Cards - nur wenn zusammenfassung vorhanden */}
            {ergebnis.zusammenfassung && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#FFF3E0] border border-[#FFE0B2] p-4 flex items-center gap-3">
                  <ArrowUp size={24} className="text-[#E65100]" />
                  <div>
                    <p className="font-mono text-xl font-medium text-[#E65100]">
                      {ergebnis.zusammenfassung.produkte_mit_preiserhoehung}
                    </p>
                    <p className="text-sm text-[#BF360C]">Preise erhöhen</p>
                  </div>
                </div>

                <div className="bg-[#E8F5E9] border border-[#C8E6C9] p-4 flex items-center gap-3">
                  <ArrowDown size={24} className="text-[#2E7D32]" />
                  <div>
                    <p className="font-mono text-xl font-medium text-[#2E7D32]">
                      {ergebnis.zusammenfassung.produkte_mit_preissenkung}
                    </p>
                    <p className="text-sm text-[#1B5E20]">Preise senken möglich</p>
                  </div>
                </div>

                <div className="bg-stone-50 border border-stone-200 p-4 flex items-center gap-3">
                  <CheckCircle size={24} className="text-[#3A4D39]" />
                  <div>
                    <p className="font-mono text-xl font-medium text-[#3A4D39]">
                      {ergebnis.zusammenfassung.produkte_preis_passt}
                    </p>
                    <p className="text-sm text-[#5C5C5C]">Preise passen</p>
                  </div>
                </div>
              </div>
            )}

            {/* Preisvorschläge Liste - nur wenn vorhanden */}
            {ergebnis.preisvorschlaege && ergebnis.preisvorschlaege.length > 0 && (
              <div className="bg-white border border-stone-200">
                <div className="p-4 border-b border-stone-200">
                  <h3 className="font-display text-lg font-semibold text-[#3A4D39]">
                    Preisvorschläge im Detail
                  </h3>
                  <p className="text-sm text-[#858585]">Sortiert nach größtem Anpassungsbedarf</p>
                </div>

                <div className="divide-y divide-stone-100">
                  {ergebnis.preisvorschlaege.map((produkt) => (
                  <div 
                    key={produkt.id} 
                    className="p-4 hover:bg-stone-50 transition-colors"
                    data-testid={`preisvorschlag-${produkt.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded ${produkt.typ === 'Speise' ? 'bg-[#FFF3E0] text-[#D96C4A]' : 'bg-[#E3F2FD] text-[#1565C0]'}`}>
                          {produkt.typ === 'Speise' ? <Utensils size={18} /> : <Wine size={18} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-[#1A1A1A]">{produkt.name}</h4>
                            <span className={`text-xs px-2 py-0.5 border ${getAnpassungClass(produkt.anpassung_richtung)}`}>
                              {produkt.anpassung_richtung === 'erhöhen' ? 'Preis erhöhen' : 
                               produkt.anpassung_richtung === 'senken' ? 'Senken möglich' : 'Passt'}
                            </span>
                          </div>
                          <p className="text-xs text-[#858585] mt-1">
                            Wareneinsatz: {formatCurrency(produkt.wareneinsatz)} · 
                            Geschätzt {produkt.geschaetzte_menge_monat} Stk./Monat
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-xs text-[#858585]">Aktuell</p>
                            <p className="font-mono text-lg">{formatCurrency(produkt.aktueller_verkaufspreis)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {getAnpassungIcon(produkt.anpassung_richtung)}
                          </div>
                          <div>
                            <p className="text-xs text-[#858585]">Vorschlag</p>
                            <p className={`font-mono text-lg font-medium ${
                              produkt.anpassung_richtung === 'erhöhen' ? 'text-[#D96C4A]' : 
                              produkt.anpassung_richtung === 'senken' ? 'text-[#2E7D32]' : 'text-[#3A4D39]'
                            }`}>
                              {formatCurrency(produkt.vorgeschlagener_preis)}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-[#858585] mt-1">
                          Marge: {formatCurrency(produkt.marge_betrag)} ({produkt.marge_prozent}%)
                        </p>
                        {produkt.preis_anpassung !== 0 && (
                          <p className={`text-xs font-mono mt-1 ${
                            produkt.preis_anpassung > 0 ? 'text-[#D96C4A]' : 'text-[#2E7D32]'
                          }`}>
                            {produkt.preis_anpassung > 0 ? '+' : ''}{formatCurrency(produkt.preis_anpassung)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Hinweis - nur wenn Berechnung vorhanden */}
            {ergebnis.berechnung && (
              <div className="mt-6 bg-[#E3F2FD] border border-[#BBDEFB] p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-[#1565C0] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#1565C0] text-sm">Hinweis zur Berechnung</p>
                    <p className="text-sm text-[#0D47A1] mt-1">
                      Die Preisvorschläge basieren auf einer geschätzten Verkaufsmenge von 30 Stück pro Produkt und Monat. 
                      Die tatsächlichen optimalen Preise können je nach Nachfrage und Wettbewerb abweichen. 
                      Der Deckungsbeitrag verteilt sich anteilig nach Wareneinsatz auf alle Produkte.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Preisvorschlag;
